import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, Typography, IconButton, Tooltip, CircularProgress } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useAuth } from "../../contexts/AuthContext";
import AiChart from "./AiChart";
import { AI_ROLES, AI_INSIGHTS_EVENT } from "./AiAssistant";
const API_URL = process.env.REACT_APP_API_URL || "/api";
// Charts pinned from the AI assistant — re-run against live data on load.
const AiInsightsSection = () => {
    const { user } = useAuth();
    const allowed = !!user && AI_ROLES.includes(user.role);
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/ai/insights`);
            setInsights(res.data.data || []);
        }
        catch {
            // leave the section empty rather than break the dashboard
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        if (!allowed)
            return;
        load();
        window.addEventListener(AI_INSIGHTS_EVENT, load);
        return () => window.removeEventListener(AI_INSIGHTS_EVENT, load);
    }, [allowed, load]);
    const remove = async (id) => {
        try {
            await axios.delete(`${API_URL}/ai/insights/${id}`);
            setInsights((prev) => prev.filter((i) => i._id !== id));
        }
        catch {
            window.alert("Could not remove insight");
        }
    };
    if (!allowed)
        return null;
    return (_jsxs(Box, { sx: { mb: 3, flexShrink: 0 }, children: [_jsx(Typography, { fontSize: "0.75rem", fontWeight: 800, color: "#334155", letterSpacing: "0.08em", mb: 1.25, children: "MY AI INSIGHTS" }), loading ? (_jsx(CircularProgress, { size: 20 })) : insights.length === 0 ? (_jsxs(Box, { sx: { p: 2, border: "1px dashed #cbd5e1", borderRadius: "12px", display: "flex", alignItems: "center", gap: 1, color: "#64748b" }, children: [_jsx(AutoAwesomeIcon, { fontSize: "small", sx: { color: "#7c3aed" } }), _jsx(Typography, { variant: "body2", children: "Need a metric that isn't here? Ask the AI assistant (bottom-right) for a chart and pin it \u2014 it will show up here with live data." })] })) : (_jsx(Box, { sx: { display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(auto-fill, minmax(380px, 1fr))" }, gap: 2 }, children: insights.map((ins) => (_jsxs(Box, { sx: { p: 2, bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px" }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }, children: [_jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { fontWeight: 700, fontSize: "0.9rem", color: "#0f172a", children: ins.title }), ins.description && _jsx(Typography, { variant: "caption", color: "#64748b", children: ins.description })] }), _jsx(Tooltip, { title: "Remove from dashboard", children: _jsx(IconButton, { size: "small", onClick: () => remove(ins._id), children: _jsx(DeleteOutlineIcon, { fontSize: "small" }) }) })] }), ins.error ? (_jsxs(Typography, { variant: "body2", color: "error", children: ["This insight could not be loaded: ", ins.error] })) : (_jsx(AiChart, { spec: { ...ins, collection: ins.collectionName } }))] }, ins._id))) }))] }));
};
export default AiInsightsSection;
